import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { captureMutationError } from '@/shared/lib/sentry-utils';
import { parseApiError } from '../parse-error';
import { POLLING_INTERVALS, queryKeys } from '../query-keys';
import {
  runBacktest,
  getBacktestRuns,
  getBacktestRun,
  getBacktestEquityCurve,
  getBacktestComparison,
  getBacktestAnnotations,
  createBacktestAnnotation,
  type BacktestRunRequest,
  type BacktestListParams,
  type BacktestAnnotationCreate,
} from '../endpoints/backtests';

export function useBacktestRuns(params: BacktestListParams = {}) {
  return useQuery({
    queryKey: queryKeys.backtests.list(params),
    queryFn: () => getBacktestRuns(params),
    staleTime: 60_000,
  });
}

export function useBacktestRun(id: number) {
  return useQuery({
    queryKey: queryKeys.backtests.detail(String(id)),
    queryFn: () => getBacktestRun(id),
    staleTime: 5 * 60_000,
    enabled: id > 0,
  });
}

export function useBacktestEquityCurve(id: number) {
  return useQuery({
    queryKey: queryKeys.backtests.equityCurve(String(id)),
    queryFn: () => getBacktestEquityCurve(id),
    staleTime: 5 * 60_000,
    enabled: id > 0,
  });
}

export function useBacktestComparison(ids: number[]) {
  return useQuery({
    queryKey: queryKeys.backtests.compare(ids.map(String)),
    queryFn: () => getBacktestComparison(ids),
    staleTime: 5 * 60_000,
    enabled: ids.length >= 2,
  });
}

export function useRunBacktest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: BacktestRunRequest) => runBacktest(body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.backtests.all() });
      toast.success('Backtest completed');
    },
    onError: (err) => {
      toast.error(parseApiError(err, 'Failed to run backtest'));
      captureMutationError(err, { mutation: 'run_backtest' });
    },
  });
}

export function useBacktestAnnotations(runId: number) {
  return useQuery({
    queryKey: queryKeys.backtests.annotations(String(runId)),
    queryFn: () => getBacktestAnnotations(runId),
    refetchInterval: POLLING_INTERVALS.backtestAnnotations,
    refetchIntervalInBackground: false,
    enabled: runId > 0,
    staleTime: 15_000,
  });
}

export function useCreateBacktestAnnotation(runId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: BacktestAnnotationCreate) => createBacktestAnnotation(runId, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.backtests.annotations(String(runId)),
      });
      toast.success('Annotation saved');
    },
    onError: (err) => {
      toast.error(parseApiError(err, 'Failed to save annotation'));
      captureMutationError(err, { mutation: 'backtest_annotation_create', run_id: runId });
    },
  });
}
